import * as React from "react";
import { AUB, INK, INK2, LINE, ACTIVE_BG } from "../../chat/chatTheme";

/**
 * Geteilte Bausteine der Reiter in der rechten Mail-Leiste.
 *
 * Damit die Optik nicht in fünf Reiter-Dateien auseinanderläuft, liegen Karte,
 * Chip, Leerzustand, Fehlerzeile, Abschnittstitel, Suchfeld und „mehr laden"
 * genau hier. Bewusst OHNE Fluent-UI-Formularkomponenten (Projektregel für neue
 * Bausteine) — die Leiste ist mit den kvp-rd-Konstanten aus `chat/chatTheme`
 * gebaut, mit denen sie schon heute gezeichnet wird.
 */

/* ── Styles (auch von den Reitern direkt verwendbar) ───────────────────── */

export const zeileStyle: React.CSSProperties = {
    display: "flex",
    flexDirection: "column",
    gap: 2,
    padding: "9px 10px",
    borderRadius: 8,
    border: `1px solid ${LINE}`,
    marginBottom: 8,
    cursor: "pointer",
    background: "#fff",
};

export const chipStyle: React.CSSProperties = {
    display: "inline-block",
    padding: "0 6px",
    borderRadius: 8,
    backgroundColor: ACTIVE_BG,
    color: AUB,
    fontSize: 10.5,
    fontWeight: 600,
    whiteSpace: "nowrap",
};

export const hinweisStyle: React.CSSProperties = {
    color: INK2,
    fontSize: 13,
    textAlign: "center",
    padding: "24px 12px",
    lineHeight: 1.5,
};

export const abschnittStyle: React.CSSProperties = {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 0.4,
    textTransform: "uppercase",
    color: INK2,
    margin: "4px 0 8px",
};

export const titelZeileStyle: React.CSSProperties = {
    fontWeight: 600,
    fontSize: 13,
    color: INK,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
};

export const linkAktionStyle: React.CSSProperties = {
    alignSelf: "flex-start",
    border: "none",
    background: "none",
    padding: 0,
    marginTop: 4,
    color: AUB,
    font: "inherit",
    fontSize: 12,
    textDecoration: "underline",
    textUnderlineOffset: 2,
    cursor: "pointer",
};

export const gestrichelterKnopfStyle: React.CSSProperties = {
    display: "block",
    width: "100%",
    marginBottom: 14,
    padding: "8px 12px",
    border: `1px dashed ${AUB}`,
    borderRadius: 8,
    background: "#fff",
    color: AUB,
    font: "inherit",
    fontSize: 12.5,
    fontWeight: 700,
    cursor: "pointer",
};

/* ── Bausteine ─────────────────────────────────────────────────────────── */

/** Eine Karte in der Reiter-Liste. Ohne `onClick` ist sie nicht anklickbar. */
export const Zeile: React.FC<{
    onClick?: () => void;
    /** Nicht klickbar, aber weiterhin sichtbar (z. B. die gerade offene Mail). */
    inaktiv?: boolean;
    titel?: string;
    children: React.ReactNode;
}> = ({ onClick, inaktiv, titel, children }) => {
    const klickbar = Boolean(onClick) && !inaktiv;
    return (
        <div
            style={{ ...zeileStyle, cursor: klickbar ? "pointer" : "default", opacity: inaktiv ? 0.75 : 1 }}
            title={titel}
            onClick={klickbar ? onClick : undefined}
            role={klickbar ? "button" : undefined}
            tabIndex={klickbar ? 0 : undefined}
        >
            {children}
        </div>
    );
};

export const Chip: React.FC<{ children: React.ReactNode; title?: string }> = ({ children, title }) => (
    <span style={chipStyle} title={title}>
        {children}
    </span>
);

/** Leerzustand / erklärender Hinweis. */
export const Hinweis: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <div style={hinweisStyle}>{children}</div>
);

/**
 * Fehlerzeile EINES Reiters (bzw. eines Blocks darin) — mit „Neu laden".
 * Bewusst nicht global: ein Fehler im Schriftwechsel darf die Termine nicht
 * mit abschalten.
 */
export const Fehlerzeile: React.FC<{ text: string; onRetry?: () => void }> = ({ text, onRetry }) => (
    <div style={{ ...hinweisStyle, color: "#b3261e" }} role="alert">
        {text}
        {onRetry && (
            <div>
                <button
                    type="button"
                    style={{ ...linkAktionStyle, alignSelf: "center", color: "#b3261e" }}
                    onClick={onRetry}
                >
                    Neu laden
                </button>
            </div>
        )}
    </div>
);

export const Abschnittstitel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <div style={abschnittStyle}>{children}</div>
);

/** Blankes Suchfeld im kvp-rd-Look (kein Fluent). */
export const Suchfeld: React.FC<{
    wert: string;
    onWert: (v: string) => void;
    platzhalter?: string;
    ariaLabel?: string;
}> = ({ wert, onWert, platzhalter, ariaLabel }) => (
    <input
        type="search"
        value={wert}
        onChange={(e) => onWert(e.target.value)}
        placeholder={platzhalter}
        aria-label={ariaLabel ?? platzhalter ?? "Suchen"}
        style={{
            width: "100%",
            boxSizing: "border-box",
            marginBottom: 10,
            padding: "7px 10px",
            border: `1px solid ${LINE}`,
            borderRadius: 8,
            background: "#fff",
            color: INK,
            font: "inherit",
            fontSize: 12.5,
            outline: "none",
        }}
    />
);

/** „weitere anzeigen" / „noch N weitere laden". */
export const MehrLadenKnopf: React.FC<{
    text: string;
    onClick: () => void;
    disabled?: boolean;
}> = ({ text, onClick, disabled }) => (
    <button
        type="button"
        style={{ ...linkAktionStyle, marginBottom: 10, opacity: disabled ? 0.6 : 1 }}
        disabled={disabled}
        onClick={onClick}
    >
        {text}
    </button>
);

/** Ladehinweis eines Reiters. */
export const Laedt: React.FC<{ text?: string }> = ({ text }) => <Hinweis>{text ?? "Lädt …"}</Hinweis>;
